/**
 * Facility Power subsection visibility for #1330 scoped focus areas.
 */

import {
  isLegacyEnabledStepsMode,
  isLegacyResolvedEnabledStepSet,
  resolveEnabledStepIds,
} from './resolveRequestFormSteps';

export type FacilityPowerReadinessSection =
  | 'readiness'
  | 'large-ups'
  | 'power-distribution';

/**
 * Pre-#1330 requests only: fold Large UPS / Power Distribution cards inside
 * Facility Power when those steps are not separate nav scopes.
 */
export function shouldFoldFacilityPowerChildSectionsInline(options: {
  storedStepIds?: string[] | null;
  enabledStepIds?: readonly string[];
}): boolean {
  if (!isLegacyEnabledStepsMode(options.storedStepIds)) {
    return false;
  }
  const enabled =
    options.enabledStepIds ?? resolveEnabledStepIds(options.storedStepIds);
  return isLegacyResolvedEnabledStepSet(enabled);
}

export function resolveFacilityPowerVisibleSections(options: {
  enabledStepIds?: readonly string[];
  /** Raw persisted value; `null` = legacy inline child sections (#1330). */
  storedStepIds?: string[] | null;
}): Set<FacilityPowerReadinessSection> {
  const enabled =
    options.enabledStepIds ?? resolveEnabledStepIds(options.storedStepIds);
  const sections = new Set<FacilityPowerReadinessSection>(['readiness']);
  const foldInline = shouldFoldFacilityPowerChildSectionsInline({
    storedStepIds: options.storedStepIds,
    enabledStepIds: enabled,
  });

  if (foldInline) {
    if (!enabled.includes('large-ups')) {
      sections.add('large-ups');
    }
    if (!enabled.includes('power-distribution')) {
      sections.add('power-distribution');
    }
  }

  return sections;
}

export const POWER_DISTRIBUTION_STAGE_GATE_MESSAGE =
  'Power Distribution fields are available after the request moves to Site Assessment.';

/**
 * Whether the Power Distribution section is available for editing.
 *
 * As of #997 this is always `true`: Power Distribution is editable in every stage
 * — including Draft — matching its sibling sections (Facility Power, Large UPS,
 * Racks, Small UPS), none of which carry a stage gate. The former DRAFT / New /
 * Initial gate (which hid the section until the request reached Site Assessment,
 * showing {@link POWER_DISTRIBUTION_STAGE_GATE_MESSAGE}) was removed so the
 * section behaves like the rest of the form from Draft onward. The `stage`
 * parameter is retained for call-site compatibility but no longer consulted.
 */
export function shouldShowPowerDistributionSection(_options?: {
  stage?: string | null;
}): boolean {
  return true;
}
